import React, { Suspense } from 'react';
import { SectionSuspenseSkeleton } from './common/loading';
import type { FilterRequest, ResourceMetricsResponse, ResourceAnalyticsResponse, OrganizationalAnalyticsResponse, BenchDetailsResponse } from '../types/api';
import type { SolverInsightsTabbedPanelProps } from './utilization/solverInsights/SolverInsightsTabbedPanel';
import {
  UtilizationMetricCardsSuspenseFallback,
  UtilizationHeadcountSectionSkeleton,
  UtilizationAnalysisSectionSkeleton,
  UtilizationOrgAnalyticsSkeleton,
  UtilizationBenchSkeleton,
  UtilizationEmployeeSectionSkeleton,
} from './utilization/UtilizationInsightsSkeletons';

// Lazy load heavy components

const UtilizationMetricCardsSection = React.lazy(() => import('./utilization/UtilizationMetricCardsSection'));
const HeadcountMetricsSection = React.lazy(() => import('./utilization/HeadcountMetricsSection'));
const UtilizationAnalysisSection = React.lazy(() => import('./utilization/UtilizationAnalysisSection'));
const OrgAnalytics = React.lazy(() => import('./utilization/OrgAnalytics'));
const BenchDetails = React.lazy(() => import('./utilization/BenchDetails'));
const EmployeeDetails = React.lazy(() => import('./utilization/EmployeeDetails'));
const SolverInsightsTabbedPanel = React.lazy(() => import('./utilization/solverInsights/SolverInsightsTabbedPanel'));
const PeopleAnalytics = React.lazy(() => import('./modules/PeopleAnalytics'));
const AdminDashboard = React.lazy(() => import('../pages/AdminDashboardPage'));
const SquadStructure = React.lazy(() => import('../pages/SquadStructurePage'));
const Requests = React.lazy(() => import('../pages/InternalProjectRequestPage'));
const ProjectsAnalytics = React.lazy(() => import('./project/ProjectAnalytics'));
const ProjectDetails = React.lazy(() => import('./project/ProjectDetails'));
const ProjectAllocationDetails = React.lazy(() => import('./project/ProjectAllocationDetails'));

// My Insights lazy components
const MyScoreCards = React.lazy(() => import('./myInsights/MyScoreCards'));
const MyUtilMetrics = React.lazy(() => import('./myInsights/MyUtilMetrics'));
const MyProjectMetrics = React.lazy(() => import('./myInsights/MyProjectMetrics'));
const MySkillsandCertsTable = React.lazy(() => import('./myInsights/MySkillsandCertsTable'));
const MyCertificationsTable = React.lazy(() => import('./myInsights/MyCertificationsTable'));
const MySkillBadgesTable = React.lazy(() => import('./myInsights/MySkillBadgesTable'));
const MyDRIInsightsTable = React.lazy(() => import('./myInsights/MyDRIInsightsTable'));
const MyDefaulterTable = React.lazy(() => import('./myInsights/MyDefaulterTable'));
const MyProjectInsightsTable = React.lazy(() => import('./myInsights/MyProjectInsightsTable'));
const MyInsightsSkillsCertsDriSection = React.lazy(() => import('./myInsights/MyInsightsSkillsCertsDriSection'));

const ComponentLoader: React.FC<{ message?: string }> = ({ message = 'Loading component…' }) => (
  <SectionSuspenseSkeleton message={message} />
);

// Wrapper components with Suspense
export interface LazyUtilizationMetricCardsSectionProps {
  filters: FilterRequest;
  ResourceMetricsData?: ResourceMetricsResponse | null;
  orgRankingData?: OrganizationalAnalyticsResponse | null;
  isLoading?: boolean;
  projectsCount?: number;
  isLoadingProjectsCount?: boolean;
}
export const LazyUtilizationMetricCardsSection: React.FC<LazyUtilizationMetricCardsSectionProps> = (props) => (
  <Suspense fallback={<UtilizationMetricCardsSuspenseFallback />}>
    <UtilizationMetricCardsSection filters={props.filters} ResourceMetricsData={props.ResourceMetricsData} orgRankingData={props.orgRankingData} isLoading={props.isLoading} projectsCount={props.projectsCount} isLoadingProjectsCount={props.isLoadingProjectsCount} />
  </Suspense>
);

export interface LazyHeadcountMetricsSectionProps {
  data?: ResourceMetricsResponse | null;
  filters?: FilterRequest;
  monthlyHeadcountData?: any[];
  monthlyAttritionData?: any[];
  isLoadingMonthly: boolean;
  isLoadingAttrition: boolean;
  isAllocationLoading?: boolean;
}
export const LazyHeadcountMetricsSection: React.FC<LazyHeadcountMetricsSectionProps> = (props) => (
  <Suspense fallback={<UtilizationHeadcountSectionSkeleton />}>
    <HeadcountMetricsSection {...props} />
  </Suspense>
);

export interface LazyUtilizationAnalysisSectionProps {
  data?: ResourceMetricsResponse | ResourceAnalyticsResponse | null;
  filters?: FilterRequest;
  isLoading?: boolean;
}
export const LazyUtilizationAnalysisSection: React.FC<LazyUtilizationAnalysisSectionProps> = (props) => (
  <Suspense fallback={<UtilizationAnalysisSectionSkeleton />}>
    <UtilizationAnalysisSection {...props} />
  </Suspense>
);

export interface LazyOrganizationalAnalyticsProps {
  data?: OrganizationalAnalyticsResponse | null;
  filters?: FilterRequest;
}
export const LazyOrganizationalAnalytics: React.FC<LazyOrganizationalAnalyticsProps> = (props) => (
  <Suspense fallback={<UtilizationOrgAnalyticsSkeleton />}>
    <OrgAnalytics {...props} />
  </Suspense>
);

export interface LazyBenchDetailsProps {
  data?: BenchDetailsResponse | null;
  filters?: FilterRequest;
}
export const LazyBenchDetails: React.FC<LazyBenchDetailsProps> = (props) => (
  <Suspense fallback={<UtilizationBenchSkeleton />}>
    <BenchDetails {...props} />
  </Suspense>
);

export const LazyEmployeeDetails: React.FC<any> = (props) => (
  <Suspense fallback={<UtilizationEmployeeSectionSkeleton />}>
    <EmployeeDetails {...props} />
  </Suspense>
);

export type LazySolverInsightsTabbedPanelProps = SolverInsightsTabbedPanelProps;
export const LazySolverInsightsTabbedPanel: React.FC<SolverInsightsTabbedPanelProps> = (props) => (
  <Suspense fallback={<UtilizationEmployeeSectionSkeleton />}>
    <SolverInsightsTabbedPanel {...props} />
  </Suspense>
);

export const LazyPeopleAnalytics: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading people analytics..." />}>
    <PeopleAnalytics {...props} />
  </Suspense>
);

export const LazyAdminDashboard: React.FC = () => (
  <Suspense fallback={<ComponentLoader message="Loading admin dashboard..." />}>
    <AdminDashboard />
  </Suspense>
);

export const LazySquadStructure: React.FC = () => (
  <Suspense fallback={<ComponentLoader message="Loading squad structure..." />}>
    <SquadStructure />
  </Suspense>
);

export const LazyRequests: React.FC = () => (
  <Suspense fallback={<ComponentLoader message="Loading requests..." />}>
    <Requests />
  </Suspense>
);

// Projects page lazy components
export interface LazyProjectsAnalyticsProps {
  analytics: any;
  healthMetrics: any;
  excludeRegionalPerformance?: boolean;
  filters: any;
}
export const LazyProjectsAnalytics: React.FC<LazyProjectsAnalyticsProps> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading analytics..." />}>
    <ProjectsAnalytics {...props} />
  </Suspense>
);

export interface LazyProjectDetailsProps {
  projectFilters: any;
  projectDetailsFilterOptions?: import('../types/api').ProjectDetailsFilterOptionsResponse;
  anomalyView?: boolean;
}
export const LazyProjectDetails: React.FC<LazyProjectDetailsProps> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading project details..." />}>
    <ProjectDetails {...props} />
  </Suspense>
);

export interface LazyProjectAllocationDetailsProps {
  projectFilters: any;
}
export const LazyProjectAllocationDetails: React.FC<LazyProjectAllocationDetailsProps> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading project allocation details..." />}>
    <ProjectAllocationDetails {...props} />
  </Suspense>
);

const ProjectDetailsAllocationSection = React.lazy(() => import("./project/ProjectDetailsAllocationSection"));

export interface LazyProjectDetailsAllocationSectionProps {
  projectFilters: any;
  projectDetailsFilterOptions?: import("../types/api").ProjectDetailsFilterOptionsResponse;
  insightTableApply?: {
    requestId: number;
    patch: Partial<import("../types/api").ProjectDetailsFilterRequest>;
  } | null;
}
export const LazyProjectDetailsAllocationSection: React.FC<LazyProjectDetailsAllocationSectionProps> = (
  props
) => (
  <Suspense fallback={<ComponentLoader message="Loading project tables..." />}>
    <ProjectDetailsAllocationSection {...props} />
  </Suspense>
);

// My Insights lazy wrappers
export const LazyMyScoreCards: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading score cards..." />}>
    <MyScoreCards {...props} />
  </Suspense>
);

export const LazyMyUtilMetrics: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading utilization metrics..." />}>
    <MyUtilMetrics {...props} />
  </Suspense>
);

export const LazyMyProjectMetrics: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading project metrics..." />}>
    <MyProjectMetrics {...props} />
  </Suspense>
);

export const LazyMySkillsandCertsTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading skills and certifications..." />}>
    <MySkillsandCertsTable {...props} />
  </Suspense>
);

export const LazyMyCertificationsTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading certifications..." />}>
    <MyCertificationsTable {...props} />
  </Suspense>
);

export const LazyMySkillBadgesTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading skill badges..." />}>
    <MySkillBadgesTable {...props} />
  </Suspense>
);

export const LazyMyDRIInsightsTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading DRI insights..." />}>
    <MyDRIInsightsTable {...props} />
  </Suspense>
);

export const LazyMyDefaulterTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading defaulter table..." />}>
    <MyDefaulterTable {...props} />
  </Suspense>
);

export const LazyMyProjectInsightsTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading project insights..." />}>
    <MyProjectInsightsTable {...props} />
  </Suspense>
);

export const LazyMyInsightsSkillsCertsDriSection: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading skills, certifications, and DRI…" />}>
    <MyInsightsSkillsCertsDriSection {...props} />
  </Suspense>
);

// My Team lazy components
const CertificationsTrendChart = React.lazy(() => import('./dri-insights/CertificationsTrendChart'));
const Tier1DRIProfilesTrendChart = React.lazy(() => import('./dri-insights/Tier1DRIProfilesTrendChart'));
const NewCompetenciesTrendChart = React.lazy(() => import('./dri-insights/NewCompetenciesTrendChart'));
const TeamScoreCards = React.lazy(() => import('./teamInsights/TeamScoreCards'));
const TeamHeadcountMetrics = React.lazy(() => import('./teamInsights/TeamHeadcountMetrics'));
const TeamUtilizationAndProjects = React.lazy(() => import('./teamInsights/TeamUtilizationAndProjects'));
const MyTeamEmployeeWiseDriSection = React.lazy(() => import('./teamInsights/MyTeamEmployeeWiseDriSection'));
const TeamDriCertsSkillsTabbedSection = React.lazy(() => import('./teamInsights/TeamDriCertsSkillsTabbedSection'));
const TeamInsightsTable = React.lazy(() => import('./teamInsights/TeamInsightsTable'));
const TeamProjectInsightsTable = React.lazy(() => import('./teamInsights/TeamProjectInsightsTable'));
const TeamInsightsTabbedSection = React.lazy(() => import('./teamInsights/TeamInsightsTabbedSection'));
const TeamCertsandSkillsTables = React.lazy(() => import('./teamInsights/TeamCertsandSkills').then(m => ({ default: m.TeamCertsandSkillsTables })));

export const LazyCertificationsTrendChart: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading certifications trend..." />}>
    <CertificationsTrendChart {...props} />
  </Suspense>
);

export const LazyTier1DRIProfilesTrendChart: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading tier 1 trend..." />}>
    <Tier1DRIProfilesTrendChart {...props} />
  </Suspense>
);

export const LazyNewCompetenciesTrendChart: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading competencies trend..." />}>
    <NewCompetenciesTrendChart {...props} />
  </Suspense>
);

export const LazyTeamScoreCards: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading score cards..." />}>
    <TeamScoreCards {...props} />
  </Suspense>
);

export const LazyTeamHeadcountMetrics: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading headcount metrics..." />}>
    <TeamHeadcountMetrics {...props} />
  </Suspense>
);

export const LazyTeamUtilizationAndProjects: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading utilization and projects..." />}>
    <TeamUtilizationAndProjects {...props} />
  </Suspense>
);

export const LazyMyTeamEmployeeWiseDriSection: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading DRI insights..." />}>
    <MyTeamEmployeeWiseDriSection {...props} />
  </Suspense>
);

export const LazyTeamDriCertsSkillsTabbedSection: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading DRI, skills, and certifications..." />}>
    <TeamDriCertsSkillsTabbedSection {...props} />
  </Suspense>
);

export const LazyTeamInsightsTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading team insights table..." />}>
    <TeamInsightsTable {...props} />
  </Suspense>
);

export const LazyTeamProjectInsightsTable: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading project insights table..." />}>
    <TeamProjectInsightsTable {...props} />
  </Suspense>
);

export const LazyTeamInsightsTabbedSection: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading team and project insights..." />}>
    <TeamInsightsTabbedSection {...props} />
  </Suspense>
);

export const LazyTeamCertsandSkillsTables: React.FC<any> = (props) => (
  <Suspense fallback={<ComponentLoader message="Loading certifications and skills..." />}>
    <TeamCertsandSkillsTables {...props} />
  </Suspense>
);

